import {ClaimTenant, VodoriJwt} from "./app.model";
import {COOKIE, STORAGE} from "./app.constants";

export class AppUtils {

  static setCookie(cname: string, cvalue: string, expDays: number = 1, path: string = '/'): void {
    const d = new Date();
    d.setTime(d.getTime() + (expDays * 24 * 60 * 60 * 1000));
    document.cookie = `${cname}=${cvalue};expires=${d.toUTCString()};path=${path};secure;`;
  }

  static getCookie(cname: string) {
    let name = `${encodeURIComponent(cname)}=`;
    let ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return AppUtils.decodeCookieValue(c.substring(name.length, c.length));
      }
    }
    return null;
  }

  /**
   * If no callback cookie is set and no 'default' defined in token, we must infer from the token a valid env/product/ten route.
   * Preference to a Cloud product, otherwise use first of all possible persona users in the token
   */
  static getPresumedRouteForToken(token: VodoriJwt): string | undefined {
    const tenantClaims: ClaimTenant[] = token['https://vodori.com/tnts'];
    let possiblePaths: string[] = [];
    tenantClaims.filter(tnt => tnt.pUsers?.length).forEach(tnt => {
      let [env, ten] = tnt.et.split('/');
      tnt.pUsers.forEach(pu => {
        let productSlug = pu.split('/')[0];
        productSlug = productSlug === 'portal' ? 'portals' : productSlug;
        possiblePaths.push(`${env}/${productSlug}/${ten}`)
      });
    })
    return possiblePaths.find(pp => pp.includes('/cloud')) ?? possiblePaths[0];
  }

  static deleteCookie(name: string, path: string = '/') {
    const expiredCookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 UTC; max-age=0;`;
    document.cookie = expiredCookie;
    document.cookie = `${expiredCookie} secure;`;
  }

  static clearCloudSessionState(): void {
    AppUtils.updateCloudSessionStorageState('true');

    const cookies = [
      COOKIE.CLOUD_CALLBACK_URI,
      COOKIE.CLOUD_JWT,
      COOKIE.CLOUD_MOST_RECENT_PATH,
      COOKIE.CLOUD_TNTS,
      COOKIE.CLOUD_ACTING_VIRTUAL_USER_ID,
    ];
    const cookiePaths = AppUtils.getCloudSessionCookiePaths();

    cookies.forEach(cookie => {
      cookiePaths.forEach(path => AppUtils.deleteCookie(cookie, path));
    });
  }

  static clearCloudSessionStorageState(): void {
    AppUtils.updateCloudSessionStorageState(null);
  }

  /**
   * Returns the tenant-scoped /ai path for an AI callback's JWT cookie.
   */
  static jwtCookiePathForCallbackUri(callbackUri: string | null): string | undefined {
    callbackUri = AppUtils.callbackPath(callbackUri);
    if (callbackUri == null) return undefined;

    let [env, uriProduct, ten] = callbackUri.split('/').filter(x => !!x);
    if (uriProduct !== 'ai' || !env || !ten) return undefined;

    return `/${env}/ai/${ten}`;
  }

  static callbackPath(callbackUri: string | null): string | null {
    if (callbackUri == null) return null;

    try {
      const url = new URL(callbackUri, window.location.origin);
      if (url.origin !== window.location.origin) {
        return null;
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  static callbackPathFromCookieOrAppState(callbackCookieUri: string | null, appStateTarget?: string | null): string | null {
    return AppUtils.callbackPath(callbackCookieUri) ?? AppUtils.callbackPath(appStateTarget ?? null);
  }

  static isAPublicPage() {
    return window.location.pathname.includes('/public/');
  }

  private static getCloudSessionCookiePaths(): string[] {
    const paths = new Set<string>(['/', '/login']);

    AppUtils.addCallbackUriCookiePaths(paths);
    AppUtils.addTenantCookiePaths(paths);

    return Array.from(paths);
  }

  private static addCallbackUriCookiePaths(paths: Set<string>): void {
    const callbackUri = AppUtils.callbackPath(AppUtils.getCookie(COOKIE.CLOUD_CALLBACK_URI));
    if (!callbackUri) {
      return;
    }

    const [env, product, tenant] = callbackUri.split('/').filter(part => !!part);
    AppUtils.addProductCookiePaths(paths, env, product, tenant);
  }

  private static addTenantCookiePaths(paths: Set<string>): void {
    AppUtils.getCookie(COOKIE.CLOUD_TNTS)?.split(',').forEach(tnt => {
      const [env, tenant] = tnt.split('/');
      AppUtils.addProductCookiePaths(paths, env, 'cloud', tenant);
      AppUtils.addProductCookiePaths(paths, env, 'ai', tenant);
    });
  }

  private static addProductCookiePaths(paths: Set<string>, env?: string, product?: string, tenant?: string): void {
    if (!env || !product || !tenant) {
      return;
    }

    const productPath = `/${env}/${product}/${tenant}`;
    paths.add(productPath);
    if (product === 'cloud') {
      paths.add(`${productPath}/flow-config`);
    }
  }

  private static updateCloudSessionStorageState(forceReloadValue: string | null): void {
    try {
      if (forceReloadValue == null) {
        window.localStorage.removeItem(STORAGE.FORCE_RELOAD);
      } else {
        window.localStorage.setItem(STORAGE.FORCE_RELOAD, forceReloadValue);
      }
      window.localStorage.removeItem(STORAGE.IDLE_EXPIRY);
    } catch (error) {
      console.warn('LOGIN: Unable to update shared logout state.', error);
    }
  }

  private static decodeCookieValue(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  /**
   * Ensure the user has a matching env/ten user for the given callbackUri.
   * Also ensure they have a persona user for the given product in the callbackUri.
   * AI callback URIs require the Cloud persona; AI separately validates tenant-route enablement.
   * Workaround the issue of portals uri mismatch to the portal product slug
   * Also allow for callbackUri to /admin/ if they have *any* personas in the env/ten. This is so that they can be sent
   * back to the Admin Console even if they don't have an "Admin" persona
   * @param callbackUri
   * @param jwt
   */
  static canAccessCallbackUri(callbackUri: string | null, jwt: VodoriJwt) {
    callbackUri = AppUtils.callbackPath(callbackUri);
    if (callbackUri == null) return false;

    let [env, uriProduct, ten] = callbackUri.split('/').filter(x => !!x);
    let isAdminCallbackUri = uriProduct === 'admin'
    let isAiCallbackUri = uriProduct === 'ai'

    let tenants = jwt['https://vodori.com/tnts'];

    let canAccess = tenants.find(t => t.et === `${env}/${ten}`)?.pUsers.some(p => {
      let puProduct = p.split('/')[0];
      puProduct = (puProduct === 'portal') ? 'portals' : puProduct;
      if (isAiCallbackUri) {
        return puProduct === 'cloud';
      }
      return uriProduct == puProduct || isAdminCallbackUri;
    });

    if (!canAccess) {
      console.log('LOGIN: ⛔️ User cannot access the cloudCallbackUri.', callbackUri)
      AppUtils.deleteCookie(COOKIE.CLOUD_CALLBACK_URI);
    }

    return canAccess;
  }
}
